import React from "react";
import { CheckCircle2 } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

// Import images
import aboutImage from "@/assets/images/about.png";
import logoImage from "@/assets/images/logo.png";

export default function About() {
  const { ref, visible } = useScrollReveal();

  const principles = [
    {
      key: "quality",
      icon: CheckCircle2,
      title: "Quality Work",
      desc: "Attention to preparation, placement and finishing."
    },
    {
      key: "dependable",
      icon: CheckCircle2,
      title: "Dependable Service",
      desc: "Clear communication and professional project execution."
    },
    {
      key: "built",
      icon: CheckCircle2,
      title: "Built to Last",
      desc: "Concrete solutions designed for strength and everyday use."
    }
  ];

  return (
    <section id="about" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <div
            ref={ref}
            className={`relative transition-all duration-700 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <div className="relative h-[420px] md:h-[520px] overflow-hidden rounded-sm">
              <img
                src={aboutImage}
                alt="Concrete worker finishing concrete with a trowel"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative rebar trace frame */}
            <div className="absolute -bottom-4 -right-4 w-32 h-32 border-4 border-[#194d32] -z-0 hidden sm:block">
              <img
                src={logoImage}
                alt="NCC Logo"
                className="w-full h-full object-contain p-3"
              />
            </div>
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-[hsl(var(--navy))] -z-0 hidden sm:block" />
          </div>

          {/* Content */}
          <div
            className={`transition-all duration-700 delay-150 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-8 h-0.5 bg-[#434951]" />
              <span className="section-label text-[#194d32]">ABOUT NCC</span>
            </div>
            <h2 className="headline text-[hsl(var(--navy))] text-4xl sm:text-5xl md:text-6xl mb-6">
              Concrete Work <br className="hidden sm:block" />
              You Can Count On
            </h2>
            <p className="text-[hsl(var(--charcoal))]/80 text-base leading-relaxed mb-4">
              Naturals Concrete Construction provides professional concrete services
              for residential and commercial projects. We work with property owners,
              builders and businesses to deliver dependable concrete solutions for
              new construction, improvements and renovations.
            </p>
            <p className="text-[hsl(var(--charcoal))]/80 text-base leading-relaxed mb-8">
              From preparation and pouring to finishing, our focus is on doing the
              job right and delivering concrete work made for long-term use.
            </p>

            {/* Principles */}
            <div className="space-y-5">
              {principles.map(({ key, icon: Icon, title, desc }) => (
                <div key={key} className="flex items-start gap-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-sm border border-[hsl(var(--accent))]/20 flex-shrink-0 bg-[#194d32]/[0.1]">
                    <Icon size={20} className="text-[#194d32]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-[hsl(var(--navy))] tracking-wide mb-0.5">
                      {title}
                    </h3>
                    <p className="text-sm text-[hsl(var(--charcoal))]/70">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}